import { useEffect, useRef, useState } from "react";
import {
  createBrowserRouter,
  RouterProvider,
} from "react-router-dom";
import { appRoutes } from "./routes";
import { trackPageView } from "./utils/analytics";

import CookieBanner from "./components/ConsentBanner";
import LimeConnectChat from "./components/LimeConnectChat";
import { registerWebMcp } from "./webmcp.js";

const appRoutesPaths = ["/portal", "/c/"];

function LimeConnectChatWrapper() {
  const [pathname, setPathname] = useState(() => router.state.location.pathname);

  useEffect(() => {
    const unsubscribe = router.subscribe((state) => {
      if (state.navigation.state === "idle") {
        setPathname(state.location.pathname);
      }
    });
    return unsubscribe;
  }, []);

  const isAppRoute = appRoutesPaths.some((path) => pathname.startsWith(path));
  if (!isAppRoute) return null;
  return <LimeConnectChat />;
}

const router = createBrowserRouter(appRoutes);

function App() {
  const lastTrackedUrlRef = useRef(null);

  useEffect(() => {
    const ac = new AbortController();
    registerWebMcp({ signal: ac.signal });
    return () => ac.abort();
  }, []);

  useEffect(() => {
    const hasConsent = () => {
      try {
        return localStorage.getItem("cookie_consent") === "accepted";
      } catch {
        return false;
      }
    };

    // Track first paint and every SPA route transition once consent is given.
    if (hasConsent()) {
      trackPageView(document.title);
    }

    const unsubscribe = router.subscribe((state) => {
      if (state.navigation.state !== "idle" || !hasConsent()) return;
      trackPageView(document.title);
    });

    return unsubscribe;
  }, []);

  useEffect(() => {
    const hasConsent = () => {
      try {
        return localStorage.getItem("cookie_consent") === "accepted";
      } catch {
        return false;
      }
    };

    const buildUrl = (locationLike) =>
      `${locationLike?.pathname ?? window.location.pathname}${locationLike?.search ?? window.location.search}${locationLike?.hash ?? window.location.hash}`;

    const trackIfNeeded = (locationLike) => {
      if (!hasConsent()) return;
      const nextUrl = buildUrl(locationLike);
      if (lastTrackedUrlRef.current === nextUrl) return;
      lastTrackedUrlRef.current = nextUrl;
      trackPageView(document.title);
    };

    // First page view after mount if consent already exists.
    trackIfNeeded(router.state.location);

    const unsubscribe = router.subscribe((state) => {
      // Ignore intermediary updates (fetchers/revalidation) unless URL changed.
      if (state.navigation.state !== "idle") return;
      trackIfNeeded(state.location);
    });

    return unsubscribe;
  }, []);

  return (
    <>
      <RouterProvider router={router} />
      <LimeConnectChatWrapper />
      <CookieBanner matomoUrl="https://www.valentin-fiess.fr/matomo" siteId="2" />
    </>
  );
}

export default App;
